import React, { memo, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _capitalize from 'lodash/capitalize';
import _cloneDeep from 'lodash/cloneDeep';
import _isNumber from 'lodash/isNumber';

import { formatNumber } from '@atc/string-fns';

import {
    Heading,
    Link,
    ListColumns,
} from 'reaxl';

import getPathSRP from '@/utilities/getPathSRP';
import {
    getPricingModelList,
    getPricingTrimList,
    getYearBasedPricingDetails,
} from '@/utilities/getPricingSEOData';

import queryDuck from '@/ducks/queryDuck.js';
import srpCrawlPathsDataMapsDuck from '@/ducks/srp/srpCrawlPathsDataMapsDuck.js';
import srpSEOPricingDuck from '@/ducks/srp/srpSEOPricingDuck.js';

const updateListingType = (str) => (typeof str === 'string' ? _capitalize(str) : str);

const buildHeaderText = (listingType = 'NEW', startYear, makeName, modelName, trimName, pricingModelList) => {
    let pricingYearHeaderBoldTxt = `${updateListingType(listingType)} ${startYear} ${makeName} ${modelName} Price by Trim`;
    let headerBoldTxt = startYear ? pricingYearHeaderBoldTxt : `${updateListingType(listingType)} ${makeName} ${modelName} Price by Year`;
    let headerTxt = 'Prices are based on the manufacturer\'s suggested retail price.';

    if (listingType !== 'NEW') {
        pricingYearHeaderBoldTxt = `${startYear} ${makeName} ${modelName} Price ${trimName ? 'by Trim' : ''} - `;
        const pricingYearHeaderTxt = `Prices are based on actual listings found on Autotrader ${trimName ? 'for each trim.' : 'across all trims.'}`;
        headerBoldTxt = startYear ? pricingYearHeaderBoldTxt : `Used ${makeName} ${modelName} ${trimName} Price By Year`;
        headerTxt = startYear ? pricingYearHeaderTxt : `Prices are based on actual listings found on Autotrader for the model years ${pricingModelList?.yearRange}.`;
    }

    return {
        headerBoldTxt,
        headerTxt,
    };
};

function PricingSEOContentContainer({ modelYearList, trimList }) {
    const dispatch = useDispatch();
    const [preFilteredSRPLink, setPreFilteredSRPLink] = useState('');

    const queryList = useSelector(queryDuck.selectors.getDuckState);
    const { listingType, makeCode, modelCode, startYear, endYear } = queryList;
    const { makesMap, modelsMap } = useSelector(srpCrawlPathsDataMapsDuck.selectors.getDuckState);
    const makeKnown = queryList?.makeCode && !Array.isArray(queryList?.makeCode);
    const modelKnown = queryList?.modelCode && !Array.isArray(queryList?.modelCode);
    const trimKnown = queryList?.trimCode && !Array.isArray(queryList?.trimCode);
    const trimName = trimKnown ? queryList?.trimCode?.split('|')?.[1] : '';

    const makeName = makeKnown && makesMap ? makesMap[queryList?.makeCode.toUpperCase()] : '';
    const modelName = modelKnown && modelsMap ? modelsMap[queryList?.modelCode] : '';

    useEffect(() => {
        if (!trimName) {
            const filteredHref = getPathSRP({
                dealType: 'greatprice',
                listingType: 'USED',
                startYear,
                endYear,
                makeCode,
                modelCode,
            });
            filteredHref.then((href) => {
                setPreFilteredSRPLink(href);
            });
        }
    }, [makeName, modelName, trimName, startYear, endYear, makeCode, modelCode]);

    const modelYearsData = useMemo(() => _cloneDeep(modelYearList), [modelYearList]);
    const trimsData = useMemo(() => _cloneDeep(trimList), [trimList]);
    const pricingModelList = getPricingModelList(modelYearsData, makeName, modelName, trimName);
    const pricingTrimList = getPricingTrimList(trimsData, makeName, modelName);
    const yearPriceData = getYearBasedPricingDetails(modelYearsData, startYear);
    const yearLabel = startYear || '';
    const greatPriceListingCount = useSelector(srpSEOPricingDuck.selectors.getGreatPriceListingCount);
    const kbbRatedPriceLink = `${yearLabel} ${makeName} ${modelName}s for sale with a KBB Great Price rating`;

    const { headerBoldTxt, headerTxt } = buildHeaderText(listingType, startYear, makeName, modelName, trimName, pricingModelList);

    useEffect(() => {
        dispatch(srpSEOPricingDuck.creators.loadGreatPriceListingCount());
    }, [dispatch]);

    const preFilterSRPLink = _isNumber(greatPriceListingCount) && greatPriceListingCount > 0 ? (
        <>
            {`We've found ${formatNumber({ value: greatPriceListingCount })} `}
            <Link
                title="KBB great price"
                href={preFilteredSRPLink}
                label={kbbRatedPriceLink}
            />
        </>
    ) : null;

    const renderYearBasedPricing = () => (
        <ListColumns
            className="list-bulleted padding-top-4 text-size-300"
            items={[
                `Low Price: ${yearPriceData?.formattedMinPrice}`,
                `Average Price: ${yearPriceData?.formattedAvgPrice}`,
                `Max Price: ${yearPriceData?.formattedMaxPrice}`,
            ]}
        />
    );

    const getModelListItems = () => pricingModelList?.links?.map((item) => {
        const rangeText = ` -  ${item?.formattedMinPrice} - ${item?.formattedMaxPrice} MSRP, depending on trim level`;
        const rangePriceText = item?.formattedMinPrice && item?.formattedMaxPrice ? rangeText : '';
        const minPriceText = item?.formattedMinPrice ? ` -  ${item?.formattedMinPrice} MSRP` : '';
        const newPriceText = trimName ? minPriceText : rangePriceText;
        const avgPriceText = item?.formattedAvgPrice ? ` -  ${item?.formattedAvgPrice} Average Price` : '';
        return (
            <>
                <Link
                    className="text-bold"
                    href={item.link}
                    title={item.value}
                >
                    {`${item.value} ${item.makeName} ${item.modelName} ${item.trimName}`}
                </Link>
                {listingType === 'NEW' ? newPriceText : avgPriceText}
            </>
        );
    });

    const getTrimListItems = () => pricingTrimList?.map((item) => {
        const avgPriceText = listingType === 'NEW' ? ` - ${item?.formattedMinPrice} MSRP` : `: ${item?.formattedAvgPrice} Average Price`;
        const priceLink = `${startYear} ${item.makeName + ' ' + item.modelName + ' ' + item.trimCode}`;
        return (
            <>
                <Link
                    className="text-bold"
                    href={item.link}
                    title={item.value}
                >
                    {priceLink}
                </Link>
                {avgPriceText}
            </>
        );
    });

    const renderPricingList = () => {
        let pricingListItems;
        if (listingType === 'NEW') {
            pricingListItems = startYear ? getTrimListItems() : getModelListItems();
        } else {
            pricingListItems = (trimName && startYear) ? getTrimListItems() : getModelListItems();
        }

        return (
            (startYear && yearPriceData && !trimName && listingType !== 'NEW') ? renderYearBasedPricing() : (
                <ListColumns
                    className="list-bulleted padding-top-4 text-size-300"
                    items={pricingListItems}
                />
            )
        );
    };

    const renderHeadline = (
        <>
            <Heading
                className="margin-bottom-3"
                size={400}
                weight="bold"
            >
                {headerBoldTxt}
            </Heading>
            <div className="text-subdued-lighter text-size-300">
                {headerTxt}
            </div>
        </>
    );

    return (
        <div className="margin-top-4 padding-horizontal-2 padding-horizontal-sm-0">
            {renderHeadline}
            {renderPricingList()}
            {!trimName && listingType !== 'NEW' && preFilterSRPLink}
        </div>
    );
}

export default memo(PricingSEOContentContainer);
